/**
 * Storefront cart drawer — header bag icon, customizer, and patch shop.
 */
(function () {
  const root = document.querySelector('[data-bbs-cart-drawer]');
  if (!(root instanceof HTMLElement)) return;

  const backdrop = root.querySelector('[data-bbs-cart-backdrop]');
  const closeBtn = root.querySelector('[data-bbs-cart-close]');
  const bodyEl = root.querySelector('[data-bbs-cart-body]');
  const footEl = root.querySelector('[data-bbs-cart-foot]');
  const cfg = parseConfig();

  /** @type {any | null} */
  let cartCache = null;
  let busy = false;

  function parseConfig() {
    const el = document.getElementById('BbsCartDrawerConfig');
    if (!el || !el.textContent) return {};
    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  function readGiftFromCart(cart) {
    const attrs = (cart && cart.attributes) || {};
    const giftApi = window.BbsCartGift;
    const on = giftApi ? giftApi.isGiftAttribute(attrs.gift) : false;
    return {
      on,
      note: String(attrs.gift_note || ''),
    };
  }

  function readGiftPayloadFromFoot() {
    if (window.BbsCartGift) return window.BbsCartGift.readGiftCartPayload(footEl);
    return { gift: '', gift_note: '', note: '' };
  }

  function syncGiftNoteVisibility() {
    const check = footEl.querySelector('[data-bbs-gift-check]');
    const wrap = footEl.querySelector('[data-bbs-gift-note-wrap]');
    if (!(check instanceof HTMLInputElement) || !(wrap instanceof HTMLElement)) return;
    wrap.hidden = !check.checked;
  }

  async function saveGiftToCart() {
    const gift = readGiftPayloadFromFoot();
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { gift: gift.gift, gift_note: gift.gift_note },
        note: gift.note,
      }),
    });
    if (!res.ok) throw new Error('gift');
    const cart = await res.json();
    cartCache = cart;
    return cart;
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = String(text ?? '');
    return div.innerHTML;
  }

  function absUrl(url) {
    const s = String(url || '');
    if (!s) return '';
    if (s.startsWith('//')) return `https:${s}`;
    return s;
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cfg.currency || 'USD',
      }).format(Number(cents) / 100);
    } catch {
      return (Number(cents) / 100).toFixed(2);
    }
  }

  function propsOf(item) {
    return (item && item.properties) || {};
  }

  function preOrderBlockHtml(p) {
    const label = String(p['Pre-order'] || '').trim();
    const ships = String(p.Ships || '').trim();
    const note = String(p['Pre-order note'] || '').trim();
    if (!label && !ships && !note) return '';
    const chipText = label || (ships ? `Ships ${ships}` : '');
    const chip = chipText
      ? `<span class="bbs-preorder-chip bbs-preorder-chip--cart"><span class="material-symbols-outlined bbs-preorder-chip__icon" aria-hidden="true">schedule</span><span class="bbs-preorder-chip__text">${escapeHtml(chipText)}</span></span>`
      : '';
    const shipsLine = label && ships ? `<p class="bbs-preorder-ships">Ships ${escapeHtml(ships)}</p>` : '';
    const noteLine = note ? `<p class="bbs-preorder-note">${escapeHtml(note)}</p>` : '';
    const details = shipsLine || noteLine ? `<div class="bbs-preorder-details">${shipsLine}${noteLine}</div>` : '';
    return `<div class="bbs-cart-line__preorder">${chip}${details}</div>`;
  }

  function designIdOf(item) {
    const p = propsOf(item);
    return String(p['Design ID'] || p['Customization ID'] || '').trim();
  }

  function isBagLine(item) {
    const p = propsOf(item);
    return p['Part'] === 'Bag' || Boolean(p['Preview front URL'] || p['Preview back URL']);
  }

  function itemImage(item) {
    const p = propsOf(item);
    if (p['Preview front URL']) return absUrl(p['Preview front URL']);
    if (item.featured_image && item.featured_image.url) return absUrl(item.featured_image.url);
    if (item.image) return absUrl(item.image);
    return '';
  }

  function closeNavDrawer() {
    const nav = document.querySelector('[data-tay-nav-drawer]');
    if (!(nav instanceof HTMLElement)) return;
    nav.dataset.open = 'false';
    nav.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bbs-nav-open');
    const toggle = document.querySelector('[data-tay-menu-open]');
    if (toggle instanceof HTMLButtonElement) toggle.setAttribute('aria-expanded', 'false');
  }

  function setOpen(open) {
    root.dataset.open = open ? 'true' : 'false';
    root.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.classList.toggle('bbs-cart-open', open);
    document.querySelectorAll('[data-bbs-cart-open]').forEach((el) => {
      if (el instanceof HTMLElement) el.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    if (open) {
      closeNavDrawer();
      void refresh(true);
      const closeEl = root.querySelector('[data-bbs-cart-close]');
      if (closeEl instanceof HTMLElement) {
        window.setTimeout(() => closeEl.focus(), 50);
      }
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart');
    return res.json();
  }

  function syncCount(cart) {
    const count = String(cart.item_count ?? 0);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(cart.item_count ?? 0) === 0);
    });
  }

  function groupCart(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const groupedIds = new Set();
    /** @type {any[]} */
    const groups = [];
    /** @type {any[]} */
    const loose = [];

    items.forEach((item) => {
      const did = designIdOf(item);
      if (!did || !isBagLine(item) || groupedIds.has(did)) return;
      groupedIds.add(did);
      const members = items.filter((it) => designIdOf(it) === did);
      const bagItem = members.find((it) => isBagLine(it)) || item;
      const patches = members.filter((it) => propsOf(it)['Part'] === 'Patch');
      const keys = members.map((it) => it.key).filter(Boolean);
      const price = members.reduce((sum, it) => sum + Number(it.final_line_price || 0), 0);
      groups.push({ type: 'design', id: did, bagItem, patches, keys, price });
    });

    items.forEach((item) => {
      const did = designIdOf(item);
      if (did && groupedIds.has(did)) return;
      loose.push(item);
    });

    return { groups, loose };
  }

  function editHref(item, designId) {
    const base = String(item.url || '');
    const id = encodeURIComponent(designId);
    return base.includes('?') ? `${base}&edit=${id}` : `${base}?edit=${id}`;
  }

  function renderDesign(group, labels) {
    const bag = group.bagItem;
    const p = propsOf(bag);
    const title = p['Design title'] || bag.product_title || labels.title;
    const front = absUrl(p['Preview front URL']);
    const back = absUrl(p['Preview back URL']);
    const img = front || itemImage(bag);
    const patchCount = group.patches.length;
    const patchWord = patchCount === 1 ? labels.patch : labels.patches;
    const variantBits = [bag.product_title];
    if (bag.variant_title && bag.variant_title !== 'Default Title') variantBits.push(bag.variant_title);
    if (patchCount > 0) variantBits.push(`${patchCount} ${patchWord}`);
    const patchList = group.patches
      .map((it) => {
        const face = propsOf(it)['Bag face'];
        const name = it.product_title || '';
        const v = it.variant_title && it.variant_title !== 'Default Title' ? ` · ${it.variant_title}` : '';
        const f = face ? ` · ${face}` : '';
        return `<li>${escapeHtml(name)}${escapeHtml(v)}${escapeHtml(f)}</li>`;
      })
      .join('');
    const previews =
      front || back
        ? `<div class="bbs-cart-previews">
            ${front ? `<span class="bbs-cart-previews__shot"><img src="${escapeHtml(front)}" alt="" width="72" height="90"><span>Front</span></span>` : ''}
            ${back ? `<span class="bbs-cart-previews__shot"><img src="${escapeHtml(back)}" alt="" width="72" height="90"><span>Back</span></span>` : ''}
          </div>`
        : '';

    return `
      <article class="bbs-cart-line bbs-cart-line--design bbs-cart-design">
        <div class="bbs-cart-line__media">
          ${img ? `<img src="${escapeHtml(img)}" alt="${escapeHtml(title)}" width="88" height="88">` : ''}
        </div>
        <div class="bbs-cart-design__body">
          <p class="bbs-cart-line__title">${escapeHtml(title)}</p>
          <p class="bbs-cart-line__variant">${escapeHtml(variantBits.join(' · '))}</p>
          ${preOrderBlockHtml(p)}
          ${previews}
          ${patchList ? `<ul class="bbs-cart-design__patches" role="list">${patchList}</ul>` : ''}
          <div class="bbs-cart-design__row">
            <div class="bbs-cart-design__actions">
              <a class="bbs-btn bbs-btn--outline-primary bbs-cart-design__btn" href="${escapeHtml(editHref(bag, group.id))}">${escapeHtml(labels.editBag)}</a>
              <button type="button" class="bbs-btn bbs-btn--ghost bbs-cart-design__btn" data-remove-keys="${escapeHtml(group.keys.join('|'))}">${escapeHtml(labels.removeBag)}</button>
            </div>
            <span class="bbs-cart-design__price">${escapeHtml(formatMoney(group.price))}</span>
          </div>
        </div>
      </article>`;
  }

  function renderLoose(item, labels) {
    const p = propsOf(item);
    const title = item.product_title || '';
    const img = itemImage(item);
    const isPatch = p['Part'] === 'Patch' || p['Order type'] === 'Patches only';
    const isPin = p['Order type'] === 'Bag pins only';
    const isSticker = p['Order type'] === 'Stickers only';
    const isCharm = p['Order type'] === 'Bag charms only';
    let variant = '';
    if (isPatch) {
      const bits = [labels.patch];
      if (p['Patch number']) bits.push(String(p['Patch number']));
      if (p['Bag face']) bits.push(String(p['Bag face']));
      if (item.variant_title && item.variant_title !== 'Default Title') bits.push(item.variant_title);
      variant = bits.join(' · ');
    } else if (isPin) {
      const bits = [labels.bagPin || 'Bag pin'];
      if (item.variant_title && item.variant_title !== 'Default Title') bits.push(item.variant_title);
      variant = bits.join(' · ');
    } else if (isSticker) {
      const bits = [labels.sticker || 'Sticker'];
      if (item.variant_title && item.variant_title !== 'Default Title') bits.push(item.variant_title);
      variant = bits.join(' · ');
    } else if (isCharm) {
      const bits = [labels.bagCharm || 'Bag charm'];
      if (item.variant_title && item.variant_title !== 'Default Title') bits.push(item.variant_title);
      variant = bits.join(' · ');
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      variant = item.variant_title;
    }
    const qty = Number(item.quantity || 0);
    return `
      <article class="bbs-cart-line${isPatch || isPin || isSticker || isCharm ? ' bbs-cart-line--patch' : ''}">
        <div class="bbs-cart-line__media">
          ${img ? `<img src="${escapeHtml(img)}" alt="${escapeHtml(title)}" width="88" height="88">` : ''}
        </div>
        <div class="bbs-cart-drawer__loose">
          <a href="${escapeHtml(item.url || '#')}" class="bbs-cart-line__title">${escapeHtml(title)}</a>
          ${variant ? `<p class="bbs-cart-line__variant">${escapeHtml(variant)}</p>` : ''}
          ${preOrderBlockHtml(p)}
          <div class="bbs-cart-drawer__qty-row">
            <div class="bbs-cart-qty">
              <button type="button" data-qty-delta="-1" data-line-key="${escapeHtml(item.key)}" aria-label="−">−</button>
              <input type="number" min="0" value="${qty}" data-line-key="${escapeHtml(item.key)}" aria-label="Quantity">
              <button type="button" data-qty-delta="1" data-line-key="${escapeHtml(item.key)}" aria-label="+">+</button>
            </div>
            <span class="bbs-cart-drawer__line-price">${escapeHtml(formatMoney(item.final_line_price))}</span>
          </div>
        </div>
      </article>`;
  }

  function renderEmpty(labels) {
    const customize = cfg.customizeUrl
      ? `<a href="${escapeHtml(cfg.customizeUrl)}" class="bbs-btn bbs-btn--primary">${escapeHtml(labels.customize)}</a>`
      : '';
    const patches = cfg.patchesUrl
      ? `<a href="${escapeHtml(cfg.patchesUrl)}" class="bbs-btn bbs-btn--outline-primary">${escapeHtml(labels.buyPatches)}</a>`
      : '';
    const pins = cfg.pinsUrl
      ? `<a href="${escapeHtml(cfg.pinsUrl)}" class="bbs-btn bbs-btn--outline-primary">${escapeHtml(labels.buyBagPins || 'Buy bag pins')}</a>`
      : '';
    const stickers = cfg.stickersUrl
      ? `<a href="${escapeHtml(cfg.stickersUrl)}" class="bbs-btn bbs-btn--outline-primary">${escapeHtml(labels.buyStickers || 'Buy stickers')}</a>`
      : '';
    return `
      <div class="bbs-cart-drawer__empty">
        <p class="bbs-lede">${escapeHtml(labels.empty)}</p>
        <div class="bbs-cart-empty-actions">${customize}${patches}${pins}${stickers}</div>
      </div>`;
  }

  function renderFoot(cart, labels) {
    if (!cart || !cart.item_count) {
      footEl.hidden = true;
      footEl.innerHTML = '';
      return;
    }
    const login =
      cfg.customerAccounts && !cfg.customer
        ? `<p class="bbs-cart-account-note">${labels.logInHtml || ''}</p>`
        : '';
    const gift = readGiftFromCart(cart);
    const giftLabel = labels.giftLabel || 'This order is a gift';
    const giftNoteLabel = labels.giftNoteLabel || 'Gift note (optional)';
    const giftNotePlaceholder = labels.giftNotePlaceholder || 'A short note for the gift tag';
    const giftNoteHint = labels.giftNoteHint || 'Optional · max 240 characters';
    const giftMax = (window.BbsCartGift && window.BbsCartGift.GIFT_NOTE_MAX) || 240;
    const giftLen = Math.min(String(gift.note || '').length, giftMax);
    const nearLimit = giftLen >= 220 ? ' is-near-limit' : '';
    footEl.hidden = false;
    footEl.innerHTML = `
      <p class="bbs-cart-drawer__subtotal-label">${escapeHtml(labels.subtotal)}</p>
      <p class="bbs-cart-drawer__subtotal">${escapeHtml(formatMoney(cart.total_price))}</p>
      <div class="bbs-cart-gift" data-bbs-cart-gift>
        <label class="bbs-cart-gift__check">
          <input type="checkbox" data-bbs-gift-check${gift.on ? ' checked' : ''}>
          <span>${escapeHtml(giftLabel)}</span>
        </label>
        <div class="bbs-cart-gift__note-wrap" data-bbs-gift-note-wrap${gift.on ? '' : ' hidden'}>
          <label class="bbs-cart-gift__note-label" for="BbsCartDrawerGiftNote">${escapeHtml(giftNoteLabel)}</label>
          <p class="bbs-cart-gift__hint">${escapeHtml(giftNoteHint)}</p>
          <textarea
            id="BbsCartDrawerGiftNote"
            class="bbs-cart-gift__note"
            data-bbs-gift-note
            rows="2"
            maxlength="${giftMax}"
            placeholder="${escapeHtml(giftNotePlaceholder)}"
            aria-describedby="BbsCartDrawerGiftNoteCount"
          >${escapeHtml(gift.note)}</textarea>
          <p id="BbsCartDrawerGiftNoteCount" class="bbs-cart-gift__count${nearLimit}" data-bbs-gift-note-count aria-live="polite">${giftLen} / ${giftMax}</p>
        </div>
      </div>
      <a class="bbs-btn bbs-btn--primary bbs-cart-drawer__checkout" href="${escapeHtml(cfg.checkoutUrl || '/checkout')}">${escapeHtml(labels.checkout)}</a>
      <button type="button" class="bbs-btn bbs-btn--ghost bbs-cart-drawer__continue" data-bbs-cart-close>${escapeHtml(labels.continue)}</button>
      ${login}
      <a class="bbs-cart-drawer__full" href="${escapeHtml(cfg.cartUrl || '/cart')}">${escapeHtml(labels.viewFull)}</a>`;
    if (window.BbsCartGift && typeof window.BbsCartGift.bindGiftUi === 'function') {
      window.BbsCartGift.bindGiftUi(footEl);
    }
  }

  function render(cart) {
    const labels = cfg.labels || {};
    if (!cart || !cart.item_count) {
      bodyEl.innerHTML = renderEmpty(labels);
      renderFoot(cart, labels);
      return;
    }
    const { groups, loose } = groupCart(cart);
    bodyEl.innerHTML = [...groups.map((g) => renderDesign(g, labels)), ...loose.map((it) => renderLoose(it, labels))].join(
      ''
    );
    renderFoot(cart, labels);
  }

  async function refresh(showLoading) {
    if (showLoading && bodyEl && !cartCache) {
      bodyEl.innerHTML = `<p class="bbs-cart-drawer__loading">${escapeHtml((cfg.labels && cfg.labels.loading) || 'Loading…')}</p>`;
    }
    try {
      cartCache = await fetchCart();
      syncCount(cartCache);
      if (root.dataset.open === 'true') render(cartCache);
    } catch {
      if (root.dataset.open === 'true') {
        bodyEl.innerHTML = `<p class="bbs-lede">${escapeHtml((cfg.labels && cfg.labels.empty) || 'Could not load your bag.')}</p>`;
      }
    }
  }

  async function updateQuantities(updates) {
    if (busy) return;
    busy = true;
    try {
      const gift = footEl && !footEl.hidden ? readGiftPayloadFromFoot() : null;
      const payload = gift
        ? {
            updates,
            attributes: { gift: gift.gift, gift_note: gift.gift_note },
            note: gift.note,
          }
        : { updates };
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const cart = await res.json();
      cartCache = cart;
      syncCount(cart);
      render(cart);
      document.dispatchEvent(
        new CustomEvent('tay:cart-updated', { bubbles: true, detail: { itemCount: cart.item_count, source: 'drawer' } })
      );
    } finally {
      busy = false;
    }
  }

  root.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (t.closest('[data-bbs-cart-close]') || t.closest('[data-bbs-cart-backdrop]')) {
      e.preventDefault();
      setOpen(false);
      return;
    }

    const checkoutLink = t.closest('.bbs-cart-drawer__checkout');
    if (checkoutLink instanceof HTMLAnchorElement) {
      e.preventDefault();
      if (busy) return;
      busy = true;
      void saveGiftToCart()
        .then(() => {
          window.location.assign(checkoutLink.href || cfg.checkoutUrl || '/checkout');
        })
        .catch(() => {
          busy = false;
          window.location.assign(checkoutLink.href || cfg.checkoutUrl || '/checkout');
        });
      return;
    }

    const removeBtn = t.closest('[data-remove-keys]');
    if (removeBtn instanceof HTMLButtonElement) {
      e.preventDefault();
      const keys = String(removeBtn.dataset.removeKeys || '')
        .split('|')
        .map((k) => k.trim())
        .filter(Boolean);
      if (!keys.length) return;
      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });
      void updateQuantities(updates);
      return;
    }

    const deltaBtn = t.closest('[data-qty-delta]');
    if (deltaBtn instanceof HTMLButtonElement) {
      e.preventDefault();
      const key = deltaBtn.dataset.lineKey;
      const delta = Number(deltaBtn.dataset.qtyDelta);
      if (!key || !Number.isFinite(delta)) return;
      const input = root.querySelector(`input[data-line-key="${CSS.escape(key)}"]`);
      const current = input instanceof HTMLInputElement ? Number(input.value) || 0 : 0;
      const next = Math.max(0, current + delta);
      void updateQuantities({ [key]: next });
    }
  });

  root.addEventListener('change', (e) => {
    const input = e.target;
    if (input instanceof HTMLInputElement && input.matches('[data-bbs-gift-check]')) {
      syncGiftNoteVisibility();
      return;
    }
    if (!(input instanceof HTMLInputElement) || !input.dataset.lineKey) return;
    const qty = Math.max(0, Math.floor(Number(input.value) || 0));
    void updateQuantities({ [input.dataset.lineKey]: qty });
  });
  document.querySelectorAll('[data-bbs-cart-open]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      setOpen(true);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.dataset.open === 'true') {
      e.stopPropagation();
      setOpen(false);
    }
  });

  document.addEventListener('tay:cart-updated', (e) => {
    const detail = e instanceof CustomEvent ? e.detail || {} : {};
    if (detail.source === 'drawer') return;
    void refresh(false).then(() => {
      if (detail.open) setOpen(true);
    });
  });

  document.addEventListener('tay:cart-open', () => setOpen(true));

  window.TAYCartDrawer = {
    open() {
      setOpen(true);
    },
    close() {
      setOpen(false);
    },
    refresh,
  };
})();
